import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, MapPin, CheckCircle } from "lucide-react";

const images = {
  hero: "https://images.unsplash.com/photo-1766392911915-b17454efe76e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvdXNlJTIwcm9vZiUyMGV4dGVyaW9yfGVufDB8fHx8MTc3Mjc5MjA2M3ww&ixlib=rb-4.1.0&q=85",
};

const primaryAreas = [
  { name: "Los Angeles", zip: "90001-90089" },
  { name: "Pasadena", zip: "91101-91199" },
  { name: "Santa Monica", zip: "90401-90411" },
  { name: "Glendale", zip: "91201-91226" },
  { name: "Burbank", zip: "91501-91526" },
  { name: "Long Beach", zip: "90801-90899" },
];

const additionalAreas = [
  "Beverly Hills", "West Hollywood", "Culver City", "Torrance",
  "Manhattan Beach", "Redondo Beach", "Hermosa Beach", "El Segundo",
  "Inglewood", "Hawthorne", "Gardena", "Carson",
  "Compton", "Downey", "Norwalk", "Whittier",
  "Alhambra", "Arcadia", "Monrovia", "Duarte",
  "San Gabriel", "Temple City", "Rosemead", "Monterey Park",
  "Malibu", "Pacific Palisades", "Brentwood", "Westwood",
  "Century City", "Mar Vista", "Venice", "Marina del Rey"
];

const servicesOffered = [
  "Emergency Roof Repair (24/7)",
  "Roof Leak Detection & Repair",
  "Storm Damage Assessment",
  "Complete Roof Replacement",
  "Shingle Repair & Replacement",
  "Commercial Roofing",
  "Free Roof Inspections",
  "Insurance Claim Assistance"
];

export default function ServiceAreas() {
  return (
    <div data-testid="service-areas-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-20">
          <img src={images.hero} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Coverage</span>
            <h1 className="heading-h1 text-white mt-2 mb-6">AREAS WE SERVE</h1>
            <p className="text-slate-300 text-lg">
              Cart Genix proudly serves the greater Los Angeles area with professional roofing services. 
              From emergency repairs to complete replacements, we're just a call away.
            </p>
          </div>
        </div>
      </section>

      {/* Primary Service Areas */}
      <section className="section-padding bg-gray" data-testid="primary-areas">
        <div className="container-custom">
          <div className="text-center mb-16 animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Primary Coverage</span>
            <h2 className="heading-h2 text-slate-900 mt-2">MAIN SERVICE AREAS</h2>
            <p className="text-body max-w-2xl mx-auto mt-4">
              Our core service areas where we provide same-day service and fastest response times.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {primaryAreas.map((area, index) => (
              <div 
                key={area.name} 
                className="bg-white p-6 rounded-lg shadow-sm border border-slate-100 hover:border-orange-500/50 transition-colors animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#F97316]" />
                  </div>
                  <div>
                    <h3 className="heading-h3 text-slate-900 mb-1">{area.name}</h3>
                    <p className="text-slate-500 text-sm">ZIP: {area.zip}</p>
                    <Link to="/contact" className="text-[#F97316] text-sm font-semibold hover:underline mt-2 inline-block">
                      Get Service Here →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Areas Grid */}
      <section className="section-padding bg-light" data-testid="all-areas">
        <div className="container-custom">
          <div className="text-center mb-16 animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Extended Coverage</span>
            <h2 className="heading-h2 text-slate-900 mt-2">ADDITIONAL SERVICE AREAS</h2>
            <p className="text-body max-w-2xl mx-auto mt-4">
              We also serve these communities throughout Los Angeles County.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {additionalAreas.map((area) => (
              <div 
                key={area} 
                className="flex items-center gap-2 text-slate-700 bg-white p-3 rounded-lg border border-slate-100"
              >
                <MapPin className="w-4 h-4 text-[#F97316] flex-shrink-0" />
                <span className="text-sm">{area}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-body mb-4">Don't see your area listed?</p>
            <Link to="/contact">
              <Button className="btn-secondary">
                Contact Us to Check Coverage
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services in All Areas */}
      <section className="section-padding bg-dark" data-testid="area-services">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeInUp">
              <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Full Service</span>
              <h2 className="heading-h2 text-white mt-2 mb-6">SERVICES AVAILABLE IN ALL AREAS</h2>
              <p className="text-slate-300 mb-8">
                No matter where you are in Los Angeles County, you have access to our complete 
                range of professional roofing services.
              </p>
              
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {servicesOffered.map((service) => (
                  <li key={service} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F97316] flex-shrink-0" />
                    <span className="text-white">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-slate-800 p-8 rounded-lg animate-fadeInUp delay-200">
              <h3 className="heading-h3 text-white mb-4">GET SERVICE IN YOUR AREA</h3>
              <p className="text-slate-400 mb-6">
                Call us now to schedule a free inspection at your location. 
                Our team serves all of Los Angeles County.
              </p>
              
              <div className="space-y-4">
                <a 
                  href="tel:+18884451244" 
                  className="flex items-center gap-4 bg-[#F97316] text-white p-4 rounded-lg hover:bg-[#EA580C] transition-colors"
                >
                  <Phone className="w-6 h-6" />
                  <div>
                    <p className="font-bold">+1-888-445-1244</p>
                    <p className="text-sm opacity-80">24/7 Emergency Line</p>
                  </div>
                </a>
                
                <Link 
                  to="/contact" 
                  className="flex items-center justify-center gap-2 bg-white text-slate-900 p-4 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
                >
                  Request Free Estimate Online
                </Link>
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-700">
                <p className="text-slate-400 text-sm">
                  <strong className="text-white">Office Location:</strong><br />
                  998 Sunset Boulevard<br />
                  Los Angeles, CA 80021
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-8 bg-slate-200">
        <div className="container-custom">
          <div className="bg-slate-300 h-64 md:h-96 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-600 font-semibold">Service Area Map</p>
              <p className="text-slate-500 text-sm">Serving all of Los Angeles County</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#F97316]">
        <div className="container-custom text-center">
          <h2 className="heading-h2 text-white mb-4">READY FOR SERVICE?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to schedule your free roof inspection. We proudly serve 
            all communities in the greater Los Angeles area.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-[#F97316] hover:bg-slate-100 font-bold h-12 px-8 rounded-md shadow-lg">
                Schedule Free Inspection
              </Button>
            </Link>
            <a href="tel:+18884451244">
              <Button className="bg-[#0F172A] hover:bg-slate-800 text-white font-bold h-12 px-8 rounded-md shadow-lg">
                <Phone className="w-4 h-4" />
                Call: 888-445-1244
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
